import React, { FC } from 'react';
import { ScrollView, StyleProp, Text, TextStyle, View } from 'react-native';
import { scale } from '@components/frame';
import i18n from 'i18n-js';
import Button from '@styles/buttons';
import ColorWrapper from '@styles/colors';
import { font } from '@styles/fonts';
import Header from '@components/settings/v2/common/SettingsHeader';
import {
  BACK_BUTTON_SETTINGS_TEXT,
  SCREEN_BORDER_RADIUS,
  SCREEN_PADDING_HORIZONTAL,
  SETTINGS_HEADER_MARGIN_BOTTOM_WITH_SHADOW_OFFSET,
  SETTINGS_ITEM_SHADOW_OFFSET,
  TEST_IDS,
} from '../../../../v2/pages/common/constants/base.constants';
import { getBoxShadowStyle } from '@styles/shadow';
import EditableLine from '@components/common/editableLine';
import { handleOpenLink, isPhone, isPhoneNative } from '@helpers';
import TouchableElement from '@components/common/touchableElement';
import type { WithColor } from '@styles/colors';
import { BULLET } from '@v2/pages/common/constants/base.constants';

import { useManageImnoo } from './hooks/useManageImnoo';

const { imnooScreen } = TEST_IDS;

type ImnooProps = WithColor;

const Imnoo: FC<ImnooProps> = ({ C }) => {
  const { imnooApiKey, setImnooApiKey, loading, imnooApiKeyInputDisabled, onUpdateImnooApiKey } = useManageImnoo();

  const shadowStyles = getBoxShadowStyle({ type: 'Field', C });

  const textStyles: StyleProp<TextStyle> = [font['Subhead 1'], { color: C('Label/Light/Primary') }];
  const textBoldStyles: StyleProp<TextStyle> = [font['Subhead 1 bold'], { color: C('Label/Light/Primary') }];

  const listBulletStyles = { marginLeft: scale(10), color: C('Label/Light/Primary'), fontSize: scale(20), transform: [{ translateY: scale(-3.5) }] };

  const listText = [
    'Enhanced Efficiency: Seamlessly combine automated quoting with live machine data to optimize workflow and reduce delays.',
    'Improved Accuracy: Utilize real-time machine performance data to refine runtime estimates, leading to more precise quotations.',
    'Increased Transparency: Gain comprehensive insights into both the quoting process and machine operations, facilitating better decision-making.',
    'Operational Optimization: Align quoting and production processes by synchronizing estimated runtimes with actual machine performance.',
  ];

  const TEXT_VERTICAL_GAP = scale(24);

  return (
    <View style={{ width: '100%', height: '100%', backgroundColor: C('Background/Light/Detail'), alignItems: 'center' }}>
      <Header
        style={{ marginBottom: scale(SETTINGS_HEADER_MARGIN_BOTTOM_WITH_SHADOW_OFFSET) }}
        backBtnText={BACK_BUTTON_SETTINGS_TEXT}
        name={'Imnoo'}
      />
      <ScrollView
        style={{
          paddingHorizontal: scale(SCREEN_PADDING_HORIZONTAL),
          paddingTop: scale(SETTINGS_ITEM_SHADOW_OFFSET),
          width: '100%',
        }}
      >
        <View testID={imnooScreen.infoSection} style={{ marginTop: scale(isPhone ? 0 : 0), gap: TEXT_VERTICAL_GAP }}>
          <Text style={textStyles}>{i18n.t("To connect Imnoo with Smart Factory's real-time machine monitoring system, follow these steps")}:</Text>
          <View style={{ flexDirection: 'row', alignItems: 'flex-start' }}>
            <Text style={textBoldStyles}>1. </Text>
            <View style={{ flex: 1 }}>
              <Text>
                <Text style={textBoldStyles}>{i18n.t('Generate your API Key')}</Text>
                <Text style={textStyles}> {i18n.t('Open your Imnoo account and visit this page to to create a new API Key')}:</Text>
              </Text>

              <TouchableElement onPress={() => handleOpenLink('https://account.imnoo.com/api-token')}>
                {({ boxStyle }) => (
                  <Text style={[boxStyle, font['Subhead 1'], { color: C('Link/Light/Primary'), marginTop: scale(4) }]}>
                    https://account.imnoo.com/api-token
                  </Text>
                )}
              </TouchableElement>
            </View>
          </View>

          <View style={{ flexDirection: 'row', alignItems: 'flex-start' }}>
            <Text style={textBoldStyles}>2. </Text>
            <View style={{ flex: 1 }}>
              <Text>
                <Text style={textBoldStyles}>{i18n.t('Paste your API key below')}</Text>
                <Text style={textStyles}> {i18n.t('Enter the generated key into the designated field below to enable seamless integration.')}</Text>
              </Text>
            </View>
          </View>
        </View>
        <View
          style={{
            backgroundColor: C('Background/Light/Secondary on detail'),
            borderRadius: scale(SCREEN_BORDER_RADIUS),
            paddingVertical: scale(15),
            marginTop: scale(24),
            flex: 1,
            ...shadowStyles,
          }}
        >
          <View
            style={{
              paddingLeft: scale(18),
              paddingRight: scale(15),
              height: scale(39),
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Text style={[font['Title 2'], { color: C('Label/Light/Primary') }]}>{i18n.t('Imnoo API Key')}</Text>
            <View testID={imnooScreen.saveButton}>
              <Button
                {...{
                  variant: 'S',
                  type: 'Primary',
                  borderRadius: scale(15),
                  onPress: onUpdateImnooApiKey,
                  disabled: imnooApiKeyInputDisabled,
                }}
              >
                {i18n.t('Save')}
              </Button>
            </View>
          </View>

          <View testID={imnooScreen.apiKeyInput} style={{ paddingHorizontal: scale(15), paddingTop: scale(6) }}>
            <EditableLine
              {...{
                item: {
                  value: imnooApiKey,
                  onChange: (v: string) => setImnooApiKey(v),
                  small: false,
                  key: 'imnooApiKey',
                  name: '',
                  placeholder: 'Update Imnoo API Key',
                  noArrow: true,
                  isLable: false,
                },
                index: 0,
                array: [{}],
                C,
                bg: 'Background/Light/Field lighter',
                topBorder: false,
                roundTop: true,
                roundBottom: true,
                editable: true,
                multiline: isPhoneNative,
              }}
              key="apiKey"
            />
          </View>
        </View>
        <View style={{ marginTop: scale(24), gap: TEXT_VERTICAL_GAP, paddingHorizontal: scale(15) }}>
          <Text style={textStyles}>
            {i18n.t(
              "In your Imnoo software generate an API Key on the settings page. Copy past this key to the field above to integrate Imnoo's AI-driven CNC calculation and quoting app with Smart Factory's real-time machine monitoring system."
            )}
          </Text>
          <Text style={textStyles}>
            {i18n.t(
              'Integrating these systems offers significant advantages for manufacturing operations. Imnoo automates the estimation of CNC machine runtimes and streamlines the quoting process, reducing manual effort and minimizing errors. Smart Factory provides real-time visibility into CNC machine status and productivity, enhancing operational oversight.'
            )}
          </Text>
          <Text style={textStyles}>{i18n.t('Benefits of Integration')}:</Text>
          {listText.map((i, index) => (
            <View key={index} style={{ flexDirection: 'row', gap: scale(6) }}>
              <Text style={listBulletStyles}>{BULLET}</Text>
              <Text style={textStyles}>{i18n.t(i)}</Text>
            </View>
          ))}
          <Text style={textStyles}>
            {i18n.t(
              'This integration empowers manufacturers to deliver accurate quotes promptly, enhance customer satisfaction, and maintain a competitive edge in the market.​'
            )}
          </Text>
        </View>
      </ScrollView>
    </View>
  );
};

export default ColorWrapper(Imnoo);
